import React, { useState } from "react";
import { toast } from "sonner";
import { Search, ShieldCheck, Lock, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { CanonicalTimeline } from "../components/case/CanonicalTimeline";
import { RiskBadge } from "../components/RiskBadge";
import { usePageTitle } from "../hooks/usePageTitle";

const slaColor = (hoursIn, meta) => {
  if (meta == null) return "text-slate-500";
  const r = hoursIn / meta;
  if (r > 1) return "text-red-600";
  if (r > 0.5) return "text-amber-600";
  return "text-green-600";
};

const PublicLookup = () => {
  usePageTitle(
    "Acompanhar meu caso",
    "Consulte o andamento do seu atendimento na RAF Direct usando o protocolo e o PIN de 6 dígitos. Não é necessário criar conta."
  );
  const [protocol, setProtocol] = useState("");
  const [pin, setPin] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const { data } = await api.post("/public/lookup", { protocol: protocol.trim(), pin: pin.trim() });
      setResult(data);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) setError("Muitas tentativas. Aguarde um minuto e tente novamente.");
      else setError(err?.response?.data?.detail || "Não foi possível encontrar o caso.");
      toast.error("Não encontrado — confira protocolo e PIN.");
      setResult(null);
    } finally { setBusy(false); }
  };

  const hoursIn = result?.official_status_since
    ? (Date.now() - new Date(result.official_status_since).getTime()) / 3_600_000
    : null;

  return (
    <div className="max-w-3xl mx-auto px-6 py-12">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Portal da Família RAF</div>
        <h1 className="font-display font-bold text-3xl md:text-4xl text-[#0B2B62] tracking-tight mt-1 flex items-center gap-3">
          <Lock className="w-8 h-8 text-slate-400" strokeWidth={1.5} /> Acompanhar meu caso
        </h1>
        <p className="text-slate-600 mt-2 max-w-xl text-sm leading-relaxed">
          Consulte o andamento do atendimento usando o <b>protocolo</b> e o <b>PIN de 6 dígitos</b> recebidos.
          Não é necessário criar conta. As informações exibidas são resumidas por segurança da família.
        </p>
      </div>

      <form onSubmit={submit} className="bg-white border border-slate-200 rounded-xl p-6 md:p-8 space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Protocolo</label>
            <input
              data-testid="lookup-protocol"
              value={protocol}
              onChange={(e) => setProtocol(e.target.value.toUpperCase())}
              placeholder="RAF-202607-ABC123"
              required
              className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm font-mono outline-none focus:ring-2 focus:ring-[#0B2B62]"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">PIN (6 dígitos)</label>
            <input
              data-testid="lookup-pin"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="000000"
              maxLength={6}
              inputMode="numeric"
              required
              className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm font-mono tracking-widest outline-none focus:ring-2 focus:ring-[#0B2B62]"
            />
          </div>
        </div>

        {error && <div data-testid="lookup-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</div>}

        <button
          data-testid="lookup-submit"
          type="submit"
          disabled={busy || !protocol || pin.length !== 6}
          className="inline-flex items-center gap-2 bg-[#0B2B62] hover:bg-slate-800 text-white font-semibold px-5 py-2.5 rounded-md text-sm disabled:opacity-60"
        >
          <Search className="w-4 h-4" /> {busy ? "Consultando…" : "Consultar caso"} <ArrowRight className="w-4 h-4" />
        </button>
        <p className="text-[11px] text-slate-400 flex items-center gap-1.5">
          <ShieldCheck className="w-3 h-3" /> 5 tentativas por minuto por IP · dados criptografados em trânsito.
        </p>
      </form>

      {result && (
        <div data-testid="lookup-result" className="mt-8 bg-white border border-slate-200 rounded-xl p-6 md:p-8">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Protocolo {result.protocol}</div>
              <h2 className="font-display font-bold text-2xl text-[#0B2B62] mt-0.5" data-testid="lookup-patient">
                {result.patient_first_name}
              </h2>
              <div className="text-sm text-slate-600 mt-1">{result.city} · {result.substance}</div>
            </div>
            <RiskBadge level={result.risk_level} size="md" />
          </div>

          {result.sla_hours != null && hoursIn != null && (
            <div className={`mt-4 text-xs font-mono ${slaColor(hoursIn, result.sla_hours)}`} data-testid="lookup-sla">
              Tempo na etapa: {hoursIn.toFixed(1)}h · meta: {result.sla_hours}h
            </div>
          )}

          <div className="mt-6">
            <CanonicalTimeline current={result.official_status} progress={result.progress_percent} />
          </div>

          <div className="mt-5 text-[11px] text-slate-500 border-t border-slate-100 pt-3">
            Última atualização de etapa: {result.official_status_since ? new Date(result.official_status_since).toLocaleString("pt-BR") : "—"}
            {result.closed && <span className="ml-2 text-green-700 font-semibold">· Caso concluído</span>}
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicLookup;
